import {
  Directive,
  ElementRef,
  HostBinding,
  Renderer2,
  OnInit,
  OnDestroy,
  Input,
  Inject,
  EventEmitter,
  Output
} from '@angular/core';
import { fromEvent, merge, Subject, Observable } from 'rxjs';
import { takeUntil, tap, first, concatMap, filter } from 'rxjs/operators';
import { DOCUMENT } from "@angular/common";
import { Position } from './position.interface';
import { Boundaries } from './boundaries.interface';

@Directive({
  selector: '[mdbDraggable]'
})
export class MdbDraggableDirective implements OnInit, OnDestroy {
  @Input() disabled = false;
  @Input() resetPosition = false;
  @Input()
  get boundTo() { return this._boundTo; }
  set boundTo(value: any) {
    if (typeof value === 'string') {
      this._boundTo = this.getHtmlElement(value);
    } else {
      this._boundTo = value;
    }
  }
  private _boundTo: HTMLElement;
  @Input()
  get handle() { return this._handle; }
  set handle(value: any) {
    if (typeof value === 'string') {
      this._handle = this.getHtmlElement(value);
      this.draggableEl = this.getHtmlElement(value);
    } else {
      this._handle = value;
      this.draggableEl = value;
    }
  }
  private _handle: HTMLElement;

  @Input() snapSensitivity = 50;
  @Input() lockAxis: string = null;
  @Input() autoScroll = true;
  @Input() scrollSpeed = 25;
  @Input() scrollSensitivity = 30;
  @Input() snapToGrid = false;
  @Input() gridSize = 1;

  @Output() dragStart = new EventEmitter<HTMLElement>();
  @Output() dragEnd = new EventEmitter<HTMLElement>();

  private elPos: Position = { x: 0, y: 0 };

  private startPos: Position;

  private boundaries: Boundaries;

  private draggableEl: HTMLElement = this.el.nativeElement;

  offsetX: Number;
  offsetY: Number;

  start$: Observable<any>;
  move$: Observable<any>;
  end$: Observable<any>;

  private destroy$ = new Subject<any>();

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: any) {}

  @HostBinding('class.mdb-draggable')
  movable = true;

  @HostBinding('class.mdb-dragging')
  dragging = false;

  private _subscribeToEvents() {
    const drag = this.start$
      .pipe(
        filter( (event: any) => event.button !== 2 ),
        concatMap( (event: any) => {
          this.onDragStart(event);
          return this.move$
            .pipe(
              tap( (moveEvent: any) => {
                this.onDragMove(moveEvent);
              }),
              takeUntil(this.end$)
            );
        }),
        takeUntil(this.destroy$)
      );

      const drop = this.start$
      .pipe(
        concatMap( () => {
          return this.end$
            .pipe(
              first(),
              tap(() => {
                this.onDragEnd();
              })
            );
        }),
        takeUntil(this.destroy$)
      );

      drag.subscribe();
      drop.subscribe();

  }

  ngOnInit() {

    this.start$ = merge(
      fromEvent(this.draggableEl, 'mousedown', { passive: false }),
      fromEvent(this.draggableEl, 'touchstart', { passive: false })
    );

    this.move$ = merge(
      fromEvent(this.document, 'mousemove', { passive: false }),
      fromEvent(this.document, 'touchmove', { passive: false })
    );

    this.end$ = merge(
      fromEvent(this.document, 'mouseup', { passive: false }),
      fromEvent(this.document, 'touchend', { passive: false }),
    );

    this._subscribeToEvents();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _resetPos() {
    this.elPos.x = 0;
    this.elPos.y = 0;
    this.renderer.setStyle(this.el.nativeElement, 'transition', 'transform ease .5s');

    this._updatePosition();
  }

  private getHtmlElement(selector: string) {
    return this.document.querySelector(selector);
  }

  private _measureBoundaries() {
    const viewRect: ClientRect = this.boundTo.getBoundingClientRect();
    const movableClientRect: ClientRect = this.el.nativeElement.getBoundingClientRect();

    this.boundaries = {
      minX: viewRect.left - movableClientRect.left + this.elPos.x,
      maxX: viewRect.right - movableClientRect.right + this.elPos.x,
      minY: viewRect.top - movableClientRect.top + this.elPos.y,
      maxY: viewRect.bottom - movableClientRect.bottom + this.elPos.y
    };
  }

  private _applyBoundaries() {
    this.elPos.x = Math.max(this.boundaries.minX, this.elPos.x);
    this.elPos.x = Math.min(this.boundaries.maxX, this.elPos.x);
    this.elPos.y = Math.max(this.boundaries.minY, this.elPos.y);
    this.elPos.y = Math.min(this.boundaries.maxY, this.elPos.y);
  }

  onDragStart(event: any) {
    event.preventDefault();
    if (event instanceof MouseEvent && event.button === 2) {
      return;
    }

    if (this.resetPosition) {
      this.renderer.removeStyle(this.el.nativeElement, 'transition');
    }

    this.dragging = true;

    if (event.type === 'mousedown') {
      this.startPos = {
        x: event.pageX - this.elPos.x,
        y: event.pageY - this.elPos.y
      };
    } else {
      this.startPos = {
        x: event.changedTouches[0].pageX - this.elPos.x,
        y: event.changedTouches[0].pageY - this.elPos.y
      };
    }

    if (this.boundTo) {
      this._measureBoundaries();
    }

    this.dragStart.emit(this.el.nativeElement);

  }

  private _updateScrollPos() {
    const elRect = this.el.nativeElement.getBoundingClientRect();

    const height = this.document.documentElement.clientHeight;
    const width = this.document.documentElement.clientWidth;


    if (elRect.top < this.scrollSensitivity) {
      this.document.documentElement.scrollTop -= this.scrollSpeed;
    }

    if (elRect.top + elRect.height > height - this.scrollSensitivity) {
      this.document.documentElement.scrollTop += this.scrollSpeed;
    }

    if (elRect.left  < this.scrollSensitivity) {
      this.document.documentElement.scrollLeft -= this.scrollSpeed;
    }

    if (elRect.left + elRect.width > width - this.scrollSensitivity) {
      this.document.documentElement.scrollLeft += this.scrollSpeed;
    }
  }

  onDragMove(event: any) {
    event.preventDefault();
    if (this.disabled) {
      return;
    }

    if (this.snapToGrid && this.gridSize > 1) {
      if (event.type === 'mousemove') {
        this.elPos.x = Math.round((event.pageX - this.startPos.x) / this.gridSize) * this.gridSize;
        this.elPos.y = Math.round((event.pageY - this.startPos.y) / this.gridSize) * this.gridSize;
      } else {
        this.elPos.x = Math.round((event.changedTouches[0].pageX - this.startPos.x) / this.gridSize) * this.gridSize;
        this.elPos.y = Math.round((event.changedTouches[0].pageY - this.startPos.y) / this.gridSize) * this.gridSize;
      }
    } else {
      if (event.type === 'mousemove') {
        this.elPos.x = event.pageX - this.startPos.x;
        this.elPos.y = event.pageY - this.startPos.y;
      } else {
        this.elPos.x = event.changedTouches[0].pageX - this.startPos.x;
        this.elPos.y = event.changedTouches[0].pageY - this.startPos.y;
      }
    }

    if (this.boundTo) {
      this._applyBoundaries();
    }

    if (this.lockAxis === 'x') {
      this.elPos.y = 0;
    }

    if (this.lockAxis === 'y') {
      this.elPos.x = 0;
    }

    if (this.autoScroll) {
      this._updateScrollPos();
    }

    this._updatePosition();
  }

  onDragEnd() {
    this.dragging = false;

    if (this.resetPosition) {
      this._resetPos();
    }

    this.dragEnd.emit(this.el.nativeElement);
  }

  private _updatePosition() {
    requestAnimationFrame(() => {
      const position = `translate(${this.elPos.x}px, ${this.elPos.y}px)`;
      this.renderer.setStyle(this.el.nativeElement, 'transform', position);
    });
  }
}
